import { useMemo } from 'react';

import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import type { ProjectType } from '../../../../../typings/enums';
import type { UseInventoryFeaturesResult } from '../types';

import { useEnhancedScanFeaturesAvailable } from './useEnhancedScanFeaturesAvailable';

/**
 * A hook for figuring out which features of the inventory dropdown should be shown for a project
 */
export function useInventoryFeaturesForProject(): UseInventoryFeaturesResult {
  const { data: project, isInitialLoading: isProjectLoading } = useCurrentProject();

  const projectTypeSet = useMemo(() => {
    if (project?.type) {
      return new Set<ProjectType>([project.type]);
    }

    return new Set<ProjectType>();
  }, [project]);

  const {
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
  } = useEnhancedScanFeaturesAvailable(projectTypeSet);

  const areAnyGatedFeaturesShown = (
    areSnippetsAvailable ||
    areVendoredDependenciesAvailable ||
    isBinaryAnalysisAvailable
  );
  const shouldShowInventory = isInventoryUIFeatureEnabled && areAnyGatedFeaturesShown;

  return useMemo(() => ({
    shouldShowInventory,
    shouldShowSnippets: areSnippetsAvailable,
    shouldShowBinaryAnalysis: isBinaryAnalysisAvailable,
    shouldShowVendored: areVendoredDependenciesAvailable,
    isInventoryUIFeatureEnabled,
    isLoading: isProjectLoading,
  }), [
    shouldShowInventory,
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
    isProjectLoading,
  ]);
}
